<template>
  <div v-if="data">
    <component :is="menuTop"/>

    <v-main>
      <div class="gr-silo">

        <!-- Migas de pan -->
        <v-container>
          <nav class="gr-migas" :aria-label="lang === 'ca' ? 'Ruta de navegació' : 'Ruta de navegación'">
            <ol>
              <li v-for="(m, i) in data.migas" :key="'m' + i">
                <router-link v-if="i < data.migas.length - 1" :to="m.url">{{ m.titulo }}</router-link>
                <span v-else aria-current="page">{{ m.titulo }}</span>
                <span v-if="i < data.migas.length - 1" class="gr-miga-sep">›</span>
              </li>
            </ol>
          </nav>
        </v-container>

        <!-- Cabecera -->
        <header class="gr-cabecera">
          <v-container>
            <p class="gr-eyebrow">
              <v-icon small color="#900A30">{{ data.icono }}</v-icon>
              {{ lang === 'ca' ? 'Serveis' : 'Servicios' }}
            </p>
            <h1 class="gr-h1">{{ data.h1 }}</h1>
            <p v-if="data.subtitulo" class="gr-subtitulo">{{ data.subtitulo }}</p>
            <p class="gr-entradilla">{{ data.entradilla }}</p>

            <!-- CTA de cabecera. Solo en las categorías que declaran heroCta -->
            <v-btn
              v-if="data.heroCta"
              :href="enlaceWhatsapp"
              target="_blank"
              rel="noopener"
              large
              depressed
              class="gr-hero-btn"
            >
              <v-icon left>mdi-whatsapp</v-icon>
              {{ data.heroCta }}
            </v-btn>
          </v-container>
        </header>

        <!-- Secciones de texto -->
        <v-container>
          <section v-for="(sec, i) in data.secciones" :key="'s' + i" class="gr-seccion">
            <h2 class="gr-h2">{{ sec.h2 }}</h2>

            <p v-for="(p, j) in (sec.parrafos || [])" :key="'p' + j" class="gr-body">{{ p }}</p>

            <ul v-if="sec.lista && sec.lista.length" class="gr-lista">
              <li v-for="(li, j) in sec.lista" :key="'l' + j">{{ li }}</li>
            </ul>

            <p v-if="sec.destacado" class="gr-destacado">{{ sec.destacado }}</p>

            <!-- Las tarjetas de las páginas hijas van detrás de la última
                 sección de texto, sea cual sea el número de secciones -->
            <template v-if="i === data.secciones.length - 1 && serviciosPublicados.length">
              <div class="gr-grupo">
                <v-row>
                  <v-col
                    v-for="serv in serviciosPublicados"
                    :key="serv.slug[lang]"
                    cols="12" sm="6" md="4"
                  >
                    <router-link :to="urlDe(serv)" class="gr-tarjeta">
                      <v-icon class="gr-tarjeta-icono">{{ serv.icono }}</v-icon>
                      <span class="gr-tarjeta-titulo">{{ serv[lang].h1 }}</span>
                      <p class="gr-tarjeta-texto">{{ serv[lang].resumen }}</p>
                      <span class="gr-tarjeta-mas">
                        {{ lang === 'ca' ? 'Més informació' : 'Más información' }} ›
                      </span>
                    </router-link>
                  </v-col>
                </v-row>
              </div>
            </template>
          </section>
        </v-container>

        <!-- ===================== BLOQUES DE SERVICIO =====================
             Los servicios descritos en la propia página, no como tarjetas que
             enlazan a páginas hijas. El primero puede marcarse como destacado. -->
        <v-container v-if="data.bloquesServicio.length">
          <section class="gr-servicios">
            <h2 v-if="data.serviciosTitulo" class="gr-h2">{{ data.serviciosTitulo }}</h2>

            <article
              v-for="(b, i) in data.bloquesServicio"
              :key="'b' + i"
              class="gr-servicio"
              :class="{ 'gr-servicio--destacado': b.destacado }"
            >
              <p v-if="b.etiqueta" class="gr-servicio-etiqueta">{{ b.etiqueta }}</p>
              <h3 class="gr-h3">{{ b.h3 }}</h3>

              <p v-for="(p, j) in (b.parrafos || [])" :key="'bp' + j" class="gr-body">{{ p }}</p>

              <ul v-if="b.lista && b.lista.length" class="gr-lista">
                <li v-for="(li, j) in b.lista" :key="'bl' + j">{{ li }}</li>
              </ul>

              <div v-for="(sub, j) in (b.subbloques || [])" :key="'bs' + j" class="gr-subbloque">
                <h4 class="gr-h4">{{ sub.h4 }}</h4>
                <p v-for="(p, k) in (sub.parrafos || [])" :key="'sp' + k" class="gr-body">{{ p }}</p>
                <ul v-if="sub.lista && sub.lista.length" class="gr-lista">
                  <li v-for="(li, k) in sub.lista" :key="'sl' + k">{{ li }}</li>
                </ul>
              </div>

              <!-- Enlaces internos: calculadoras y páginas del propio silo.
                   Reparten autoridad hacia dentro y ahorran una consulta. -->
              <p v-if="b.enlaces && b.enlaces.length" class="gr-enlaces">
                <router-link
                  v-for="(e, j) in b.enlaces"
                  :key="'be' + j"
                  :to="e.url"
                  class="gr-enlace"
                >
                  <v-icon small>{{ e.icono || 'mdi-calculator-variant-outline' }}</v-icon>
                  {{ e.texto }}
                </router-link>
              </p>

              <v-btn
                v-if="b.cta"
                :href="waDe(b.ctaWhatsapp || data.ctaWhatsapp)"
                target="_blank"
                rel="noopener"
                depressed
                class="gr-servicio-btn"
              >
                <v-icon left small>mdi-whatsapp</v-icon>{{ b.cta }}
              </v-btn>
            </article>
          </section>
        </v-container>

        <!-- ===================== PROCESO ===================== -->
        <v-container v-if="data.proceso">
          <section class="gr-proceso">
            <h2 class="gr-h2">{{ data.proceso.h2 }}</h2>
            <p v-if="data.proceso.intro" class="gr-body">{{ data.proceso.intro }}</p>

            <ol class="gr-pasos">
              <li v-for="(paso, i) in data.proceso.pasos" :key="'pa' + i" class="gr-paso">
                <span class="gr-paso-num" aria-hidden="true">{{ i + 1 }}</span>
                <div>
                  <h3 class="gr-paso-titulo">{{ paso.h3 }}</h3>
                  <p class="gr-paso-texto">{{ paso.texto }}</p>
                </div>
              </li>
            </ol>
          </section>
        </v-container>

        <!-- ===================== RESEÑAS ===================== -->
        <v-container v-if="data.resenas">
          <section class="gr-resenas">
            <h2 class="gr-h2">{{ data.resenas.h2 }}</h2>
            <p v-if="data.resenas.intro" class="gr-body">{{ data.resenas.intro }}</p>

            <v-row>
              <v-col
                v-for="(r, i) in data.resenas.items"
                :key="'r' + i"
                cols="12" md="6"
              >
                <figure class="gr-resena">
                  <blockquote class="gr-resena-texto">{{ r.texto }}</blockquote>
                  <figcaption class="gr-resena-autor">{{ r.autor }}</figcaption>
                </figure>
              </v-col>
            </v-row>
          </section>
        </v-container>

        <!-- Preguntas frecuentes. En acordeón, pero el HTML que ve Google lo
             genera scripts/prerender.js con todas las respuestas visibles. -->
        <v-container v-if="data.faqs.length">
          <section class="gr-faqs">
            <h2 class="gr-h2">{{ lang === 'ca' ? 'Preguntes freqüents' : 'Preguntas frecuentes' }}</h2>
            <v-expansion-panels accordion flat class="gr-faq-panels">
              <v-expansion-panel v-for="(f, i) in data.faqs" :key="'f' + i" class="gr-faq">
                <v-expansion-panel-header class="gr-faq-q">
                  <h3 class="gr-faq-q-texto">{{ f.q }}</h3>
                </v-expansion-panel-header>
                <v-expansion-panel-content eager>
                  <p class="gr-faq-a">{{ f.a }}</p>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </section>
        </v-container>

        <!-- Formulario de presupuesto de seguro. Solo en el silo de seguros. -->
        <v-container v-if="data.formularioSeguro">
          <formulario-seguro
            :lang="lang"
            :whatsapp="data.whatsapp"
            :origen="origenFormulario"
          />
        </v-container>

        <!-- Llamada a la acción -->
        <v-container>
          <section class="gr-cta-bloque">
            <h2 class="gr-cta-titulo">{{ data.ctaTitulo }}</h2>
            <p class="gr-cta-texto">{{ data.ctaTexto }}</p>
            <v-btn
              :href="enlaceWhatsapp"
              target="_blank"
              rel="noopener"
              x-large
              depressed
              class="gr-cta-btn"
            >
              <v-icon left>mdi-whatsapp</v-icon>
              {{ lang === 'ca' ? 'Escriu-nos per WhatsApp' : 'Escríbenos por WhatsApp' }}
            </v-btn>
            <!-- El número de seguros es solo de WhatsApp: allí no se ofrece llamar -->
            <a v-if="!data.soloWhatsapp" :href="'tel:' + telefono" class="gr-cta-tel">
              <v-icon small color="#fff">mdi-phone</v-icon>
              {{ telefonoTexto }}
            </a>
          </section>

          <!-- Enlazado horizontal entre silos -->
          <section class="gr-otros-silos">
            <h2 class="gr-otros-titulo">
              {{ lang === 'ca' ? 'També ens ocupem de' : 'También nos ocupamos de' }}
            </h2>
            <router-link
              v-for="silo in otrosSilos"
              :key="silo.id"
              :to="silo.url"
              class="gr-otro-silo"
            >
              <v-icon small>{{ silo.icono }}</v-icon>
              {{ silo.titulo }}
            </router-link>
          </section>
        </v-container>

      </div>
    </v-main>

    <component :is="menuBottom"/>
    <component :is="footer"/>
  </div>
</template>

<script>
  import seo from '@/mixins/seo';
  import contenido from '@/content';

  import MenuTopEsp from '@/components/MenuTopEsp';
  import MenuTopCat from '@/components/MenuTopCat';
  import MenuBottomEsp from '@/components/MenuBottomEsp';
  import MenuBottomCat from '@/components/MenuBottomCat';
  import FooterEsp from '@/components/FooterEsp';
  import FooterCat from '@/components/FooterCat';
  import FormularioSeguro from '@/components/FormularioSeguro';

  export default {
    name: 'PageCategoria',

    mixins: [seo],

    components: {
      FormularioSeguro
    },

    props: {
      categoriaId: { type: String, required: true },
      lang: { type: String, default: 'es' }
    },

    computed: {
      data() {
        return contenido.datosCategoria(this.categoriaId, this.lang);
      },

      /* El mixin de SEO lee de aquí */
      seoData() {
        return this.data;
      },

      /* Todas las páginas hijas publicadas, en una sola rejilla y sin separar
         por grupo: con pocas publicadas, un título por grupo dejaba cada
         tarjeta sola en su fila. El orden de los grupos se respeta. */
      serviciosPublicados() {
        return this.data.grupos.reduce(function (acc, grupo) {
          return acc.concat(grupo.servicios);
        }, []);
      },

      telefono() {
        return (this.data && this.data.telefono) || contenido.SITE.telefono;
      },

      telefonoTexto() {
        return (this.data && this.data.telefonoTexto) || contenido.SITE.telefonoTexto;
      },

      /* Asunto del correo del formulario: identifica la página de origen */
      origenFormulario() {
        return this.data.categoria.id + ' · ' + this.data.h1;
      },

      enlaceWhatsapp() {
        return 'https://wa.me/' + this.data.whatsapp +
          '?text=' + encodeURIComponent(this.data.ctaWhatsapp);
      },

      otrosSilos() {
        var actual = this.categoriaId;
        return contenido.navegacionSilos(this.lang).filter(function (s) {
          return s.id !== actual;
        });
      },

      menuTop() { return this.lang === 'ca' ? MenuTopCat : MenuTopEsp; },
      menuBottom() { return this.lang === 'ca' ? MenuBottomCat : MenuBottomEsp; },
      footer() { return this.lang === 'ca' ? FooterCat : FooterEsp; }
    },

    methods: {
      urlDe(servicio) {
        return contenido.urlServicio(this.data.categoria, servicio, this.lang);
      },

      /* Enlace de WhatsApp con un mensaje concreto, para los CTA de cada
         bloque de servicio. Así Alex sabe de qué trámite le escriben. */
      waDe(mensaje) {
        return 'https://wa.me/' + this.data.whatsapp +
          '?text=' + encodeURIComponent(mensaje);
      }
    }
  };
</script>

<style src="@/assets/scss/silo.scss" lang="scss"></style>
